import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';
import { usePatientCockpitData } from '../../hooks/usePatientCockpit';
import { useAuth } from '../../hooks/useAuth';
import { useOrganizationStore } from '../../stores/organizationStore';
import LoadingSpinner from '../LoadingSpinner';
import { TranscriptModal } from '../TranscriptModal';

// Import cockpit components
import HeroSection from './HeroSection';
import QuickActions from './QuickActions';
import AppointmentHistory from './AppointmentHistory';
import ClinicalIntelligence from './ClinicalIntelligence';

/**
 * PatientCockpit - Main dashboard for comprehensive patient care management
 * Replaces the traditional patient detail page with a cockpit-style interface
 * optimized for clinical workflows and decision-making
 */
const PatientCockpit: React.FC = () => {
  const { id: patientId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { operationMode } = useOrganizationStore();
  
  // Transcript viewing state
  const [selectedTranscriptId, setSelectedTranscriptId] = useState<string | null>(null);
  const [isTranscriptModalOpen, setIsTranscriptModalOpen] = useState(false);

  // Fetch all cockpit data using the composite hook
  const {
    patient,
    patientLoading,
    patientError,
    appointments,
    appointmentsLoading,
    appointmentsError,
    appointmentStats,
    clinicalInsights,
    medicalAlerts,
    isLoading,
    hasError,
    errorMessage,
    refetchPatient,
    refetchAppointments
  } = usePatientCockpitData(patientId!);

  // Handle transcript viewing
  const handleViewTranscript = (appointmentId: string) => {
    setSelectedTranscriptId(appointmentId);
    setIsTranscriptModalOpen(true);
  };

  const handleCloseTranscript = () => {
    setSelectedTranscriptId(null);
    setIsTranscriptModalOpen(false);
  };

  // Loading state
  if (isLoading && !patient) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Loading patient cockpit...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError && !patient) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-red-800">
                Error Loading Patient Cockpit
              </h3>
              <p className="text-sm text-red-600 mt-1">{errorMessage}</p>
              <div className="mt-4 flex space-x-3">
                <button
                  onClick={refetchPatient}
                  className="bg-red-100 px-4 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/patients')}
                  className="bg-white px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-50"
                >
                  Back to Patients
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-yellow-800">
                Patient Not Found
              </h3>
              <p className="text-sm text-yellow-600 mt-1">
                The requested patient could not be found.
              </p>
              <button
                onClick={() => navigate('/patients')}
                className="mt-3 bg-yellow-100 px-4 py-2 rounded-md text-sm font-medium text-yellow-800 hover:bg-yellow-200"
              >
                Back to Patients
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Navigation Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/patients')}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Patients
        </button>
        
        <div className="text-sm text-gray-500">
          Last updated: {new Date().toLocaleTimeString()}
        </div>
      </div>

      {/* Connected Mode Notice */}
      {operationMode === 'connected' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-2 h-2 bg-blue-400 rounded-full mr-3"></div>
            <div>
              <p className="text-sm text-blue-800">
                <strong>Connected Mode:</strong> Patient data is synchronized from your EHR system. 
                Some actions may be read-only to maintain data integrity.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Cockpit Grid Layout */}
      <div className="cockpit-grid">
        {/* Hero Section - Full Width */}
        <div className="hero-section">
          <HeroSection patient={patient} />
        </div>

        {/* Left Column - Appointment History */}
        <div className="recent-activity">
          <AppointmentHistory
            patientId={patient.id}
            onViewTranscript={handleViewTranscript}
            limit={5}
          />
        </div>

        {/* Right Column - Quick Actions */}
        <div className="quick-actions">
          <QuickActions
            patient={patient}
            onTranscriptView={handleViewTranscript}
          />
        </div>

        {/* Bottom Section - Clinical Intelligence */}
        <div className="clinical-intelligence">
          <ClinicalIntelligence
            appointments={appointments}
            patientId={patient.id}
          />
        </div>
      </div>

      {/* Transcript Modal */}
      {selectedTranscriptId && (
        <TranscriptModal
          onClose={handleCloseTranscript}
          appointmentId={selectedTranscriptId}
        />
      )}

      {/* HIPAA Compliance Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-800">
              <strong>HIPAA Notice:</strong> Access to this patient record has been logged. 
              All patient information is encrypted and access is monitored for compliance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientCockpit;